import { Constants } from '@pwc/pwc-deals-web-common';
import classNames from 'classnames';
import _differenceBy from 'lodash/differenceBy';
import _clone from 'lodash/clone';
import _union from 'lodash/union';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch } from 'react-redux';
import ModalConfirmation from '../../common/components/ModalConfirmation';
import {
  contentShape,
  contentTypeAnalyses,
  contentTypeHypotheses,
  contentTypeInsights,
  contentTypeShape,
  sortAscending,
} from '../../common/components/proptypes/commonProptypes';
import { structuredAnalysisLinkToDisplay } from '../../common/engine/utilities/structured-data-utilities';
import { useKfaTitle } from '../../common/hooks/useKfaTitle';
import { showModalError } from '../../common/redux/errors/errorsSlice';
import userTokenService from '../../common/services/security/userTokenService';
import {
  editAnalysisPath,
  editHypothesisPath,
  editInsightPath,
  isStructuredAnalysis2ContentType,
  viewAnalysisPath,
  viewHypothesisPath,
  viewInsightPath,
  viewStructuredAnalysisPathForSubType,
} from '../../common/services/urlService';
import styles from './ContentList.module.scss';
import ContentListCard from './ContentListCard';
import ContentListHeader from './ContentListHeader';
import { sortKeys } from './contentListSortKeys';

const ContentList = ({
  contentType,
  projectId,
  contentItems,
  onDelete,
  onReorder,
  onSort,
  sortKey,
  sortDir,
  updateSortOrderApiCall,
  isFiltered,
  dataLength,
  hasMore,
  next,
  allowReorder,
}) => {
  const { t } = useTranslation();
  const kfaTitle = useKfaTitle();
  const dispatch = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDragAllowed, setIsDragAllowed] = useState(false);
  const [isDragEnabled, setIsDragEnabled] = useState(false);
  const [dragTarget, setDragTarget] = useState(null);
  const [isInternalUser, setIsInternalUser] = useState(false);
  const [localContentItems, setLocalContentItems] = useState(contentItems);
  const [selectedContentItems, setSelectedContentItems] = useState([]);

  useEffect(() => {
    setLocalContentItems(contentItems);
  }, [contentItems]);

  useEffect(() => {
    if (isFiltered) {
      setSelectedContentItems([]);
    }
  }, [isFiltered]);

  useEffect(() => {
    const internalUserRoles = Constants.USER_ROLE_GROUPS.INTERNAL;

    const projectRole = userTokenService.getProjectTokenProperty(
      projectId,
      'role'
    );
    const permissionAllowsDrag = userTokenService.hasProjectPermission(
      projectId,
      [
        Constants.USER_PERMISSIONS.MANAGE_CONTENT,
        Constants.USER_PERMISSIONS.APPROVE,
        Constants.USER_PERMISSIONS.CONTRIBUTE,
      ]
    );
    const sortAllowsDrag =
      sortKey === sortKeys.sortOrder && sortDir === sortAscending;
    const dragAllowed = permissionAllowsDrag && sortAllowsDrag && !isFiltered;
    if (allowReorder === false) {
      setIsDragAllowed(false);
      setIsDragEnabled(false);
    } else {
      setIsDragAllowed(dragAllowed);
      setIsDragEnabled(permissionAllowsDrag);
    }
    setIsInternalUser(internalUserRoles.includes(projectRole));
  }, [sortKey, sortDir, isFiltered, projectId, allowReorder]);

  const handleHideModal = () => {
    setIsModalVisible(false);
    setDeleteTarget(null);
  };

  const handleDeleteMenuClick = (contentItem) => {
    setIsModalVisible(true);
    setDeleteTarget(contentItem);
  };

  const handleDelete = () => {
    setIsModalVisible(false);
    onDelete(deleteTarget);
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const updatedSelectedContentItems = localContentItems.map(
        (item, index) => ({
          index,
          contentThreadId: item.contentThreadId,
        })
      );
      setSelectedContentItems(updatedSelectedContentItems);
    } else {
      setSelectedContentItems([]);
    }
  };

  const handleSelect = (contentThreadId, isSelected, index) => {
    if (isSelected) {
      const updatedSelected = _union(selectedContentItems, [
        { index, contentThreadId },
      ]);
      setSelectedContentItems(updatedSelected);
    } else {
      const updatedSelected = selectedContentItems.filter(
        (item) => item.contentThreadId !== contentThreadId
      );
      setSelectedContentItems(updatedSelected);
    }
  };

  const handleSortKey = (key) => {
    if (key !== sortKey) {
      onSort(key, null);
    }
  };

  const handleSortDir = (dir) => {
    if (dir !== sortDir) {
      onSort(null, dir);
    }
  };

  const isAscendingSort = () => sortDir === sortAscending;

  const handleBeforeCapture = ({ draggableId }) => {
    const dragTargetContentThreadId = parseInt(draggableId, 10);
    setDragTarget(dragTargetContentThreadId);
    const filteredSelectedContentItems = selectedContentItems.filter(
      (item) => item.contentThreadId === dragTargetContentThreadId
    );
    if (
      selectedContentItems.length > 0 &&
      filteredSelectedContentItems.length === 0
    ) {
      setSelectedContentItems([]);
    }
  };

  const handleDragEnd = async (dragEnd) => {
    setDragTarget(null);

    // eslint-disable-next-line prefer-const
    let draggedItemSourceIndex = dragEnd.source.index;
    const draggedItemTargetIndex = dragEnd.destination.index;

    const hasValidDropTarget =
      !!dragEnd.destination ||
      draggedItemSourceIndex === draggedItemTargetIndex;
    if (!hasValidDropTarget) {
      return;
    }

    // Just in case the sort API call fails:
    const originalContentItems = [...localContentItems];

    let draggedList;

    if (selectedContentItems.length > 0) {
      const itemsToMove = localContentItems.filter((_) => {
        const filteredSelectedContentItems = selectedContentItems.filter(
          (item) => item.contentThreadId === _.contentThreadId
        );
        return filteredSelectedContentItems.length > 0;
      });
      draggedList = _differenceBy(localContentItems, itemsToMove);
      let insertIndex;
      // Drag target is being moved down in the list
      if (draggedItemSourceIndex < draggedItemTargetIndex) {
        const sortedSelectedContentItems = _clone(selectedContentItems);
        sortedSelectedContentItems.sort((a, b) => a.index - b.index);
        draggedItemSourceIndex = sortedSelectedContentItems[0].index;
        const targetContentThreadId =
          localContentItems[draggedItemTargetIndex].contentThreadId;
        const targetContentIndexInKeepList = draggedList.findIndex(
          (h) => h.contentThreadId === targetContentThreadId
        );
        insertIndex =
          draggedItemTargetIndex === 0 ? 0 : targetContentIndexInKeepList + 1;
      }
      // Drag target is being moved up in the list
      else {
        insertIndex = draggedItemTargetIndex;
      }

      draggedList.splice(insertIndex, 0, ...itemsToMove);
    } else {
      draggedList = [...localContentItems];
      const [movedItem] = draggedList.splice(draggedItemSourceIndex, 1);
      draggedList.splice(draggedItemTargetIndex, 0, movedItem);
    }

    const indexedList = draggedList.map((item, i) => {
      const order = isAscendingSort() ? i : draggedList.length - 1 - i;
      return {
        ...item,
        sortOrder: order,
      };
    });

    // Save the data to a new array before starting manipulation.
    const dataPreChangePosition = [...localContentItems];
    // Determine the index where the change originated > FROM.
    const startIndex = Math.min(draggedItemSourceIndex, draggedItemTargetIndex);
    // Determine the index where the change ended > TO.
    const endIndex = Math.max(
      draggedItemSourceIndex +
        (draggedItemSourceIndex > draggedItemTargetIndex &&
        selectedContentItems.length
          ? selectedContentItems.length - 1
          : 0),
      draggedItemTargetIndex
    );
    // Select the items affected by change using FROM and TO we just determined.
    const changedPositionForElements = indexedList.filter(
      (_, i) => i >= startIndex && i <= endIndex
    );

    const apiFriendlyData = changedPositionForElements
      .map((el) => el.contentThreadId)
      .map((itemId) => {
        return {
          contentThreadId: itemId,
          from: dataPreChangePosition.findIndex(
            (item) => item.contentThreadId === itemId
          ),
          to: indexedList.findIndex((item) => item.contentThreadId === itemId),
        };
      });

    // Update the frontend:
    setLocalContentItems(indexedList);

    const shouldCallApi = apiFriendlyData.some((item) => item.from !== item.to);

    if (shouldCallApi) {
      try {
        // Call API asynchronously:
        const response = await updateSortOrderApiCall(
          projectId,
          apiFriendlyData
        );

        if (response) {
          onReorder(indexedList);
        } else {
          throw new Error('Unable to save sort order');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        setLocalContentItems(originalContentItems);
        dispatch(
          showModalError(Constants.ModalErrorType.UNABLE_TO_SAVE_SORT_ORDER)
        );
      }
    }

    setSelectedContentItems([]);
  };

  let viewPathCreator;
  let editPathCreator;
  let linkAriaLabel;
  let deleteModalMessage;
  switch (contentType) {
    case contentTypeHypotheses:
      linkAriaLabel = t('viewContent', { contentName: t('keyFocusArea') });
      viewPathCreator = viewHypothesisPath;
      editPathCreator = editHypothesisPath;
      deleteModalMessage = t('contentPermanentlyDeleted', {
        contentName: kfaTitle,
      });
      break;

    case contentTypeInsights:
      linkAriaLabel = t('viewContent', { contentName: t('insight') });
      viewPathCreator = viewInsightPath;
      editPathCreator = editInsightPath;
      deleteModalMessage = t('contentPermanentlyDeleted', {
        contentName: t('insight'),
      });
      break;

    case contentTypeAnalyses:
      linkAriaLabel = t('viewContent', { contentName: t('analysis') });
      viewPathCreator = (_, contentThreadId, contentItem) => {
        if (
          contentItem.type &&
          contentItem.contentSubType &&
          isStructuredAnalysis2ContentType(contentItem.type)
        ) {
          return viewStructuredAnalysisPathForSubType(
            projectId,
            contentItem.contentSubType
          );
        }
        if (contentItem.isUnStructured) {
          return viewAnalysisPath(projectId, contentThreadId);
        }
        return structuredAnalysisLinkToDisplay(
          projectId,
          contentItem.title,
          contentItem.subType
        );
      };
      editPathCreator = editAnalysisPath;
      deleteModalMessage = t('contentPermanentlyDeleted', {
        contentName: t('analysis'),
      });
      break;

    default:
      linkAriaLabel = '';
      viewPathCreator = () => {};
      editPathCreator = () => {};
      deleteModalMessage = '';
  }

  const areSomeSelected = selectedContentItems.length > 0;
  const areAllSelected =
    localContentItems.length === selectedContentItems.length;

  const listClassNames = classNames(styles['content-list'], {
    'drag-enabled': isDragEnabled,
    'in-drag-mode': dragTarget !== null,
  });

  const isItemSelected = (contentThreadId) => {
    const selected =
      selectedContentItems.filter(
        (item) => item.contentThreadId === contentThreadId
      ).length > 0;
    return selected;
  };

  return (
    <>
      <ModalConfirmation
        show={isModalVisible}
        onCancel={handleHideModal}
        onConfirm={handleDelete}
        confirmTitle={t('areyouSureDeleteMsg')}
        confirmationMessage={deleteModalMessage}
        confirmButtonText={t('yesDelete')}
        dialogClassName="modal-confirmation"
        contentClassName="modal-confirmation"
      />
      <div className={listClassNames} data-testid="content-list">
        {localContentItems.length > 0 && (
          <ContentListHeader
            projectId={projectId}
            currentSortKey={sortKey}
            currentSortDir={sortDir}
            setCurrentSortKey={handleSortKey}
            setCurrentSortDir={handleSortDir}
            isDragAllowed={isDragAllowed}
            isDragEnabled={isDragEnabled}
            areSomeSelected={areSomeSelected}
            areAllSelected={areAllSelected}
            onSelectAll={handleSelectAll}
          />
        )}

        <DragDropContext
          onDragEnd={handleDragEnd}
          onBeforeCapture={handleBeforeCapture}
        >
          <Droppable droppableId="content-list">
            {(provided) => {
              return (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {localContentItems.length > 0 && (
                    <InfiniteScroll
                      dataLength={dataLength}
                      hasMore={hasMore}
                      next={next}
                      scrollThreshold={0.75}
                      className="content-list-infinite-scroll"
                    >
                      {localContentItems.map((item, index) => (
                        <ContentListCard
                          contentType={contentType}
                          contentItem={item}
                          index={index}
                          projectId={projectId}
                          onDeleteMenuClick={handleDeleteMenuClick}
                          onSelect={handleSelect}
                          selectedCount={selectedContentItems.length}
                          areAllSelected={areAllSelected}
                          isSelected={isItemSelected(item.contentThreadId)}
                          isDragTarget={dragTarget === item.contentThreadId}
                          isDragAllowed={isDragAllowed}
                          isDragEnabled={isDragEnabled}
                          isInternalUser={isInternalUser}
                          viewPathCreator={viewPathCreator}
                          editPathCreator={editPathCreator}
                          linkAriaLabel={linkAriaLabel}
                          key={item.contentThreadId}
                        />
                      ))}
                    </InfiniteScroll>
                  )}
                  {provided.placeholder}
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>
      </div>
    </>
  );
};

ContentList.propTypes = {
  contentType: contentTypeShape.isRequired,
  projectId: PropTypes.string.isRequired,
  contentItems: PropTypes.arrayOf(contentShape),
  onDelete: PropTypes.func,
  onReorder: PropTypes.func.isRequired,
  updateSortOrderApiCall: PropTypes.func.isRequired,
  isFiltered: PropTypes.bool,
  dataLength: PropTypes.number.isRequired,
  hasMore: PropTypes.bool.isRequired,
  next: PropTypes.func.isRequired,
};

ContentList.defaultProps = {
  contentItems: [],
  onDelete: () => {},
  isFiltered: false,
};

export default ContentList;
